import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { enqueueCapture, getAllCaptures, countCaptures, removeCapture } from '../utils/offlineQueue';
import { isNative } from '../lib/native/platform';
import { addNetworkListener, getOnlineStatus } from '../lib/native/network';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfflineContext = createContext(null);

export const useOffline = () => {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
};

const endpointFor = (item) => {
  if (item.type === 'object-intel') return `${API}/projects/${item.projectId}/object-intel/run`;
  if (item.type === 'live-measure') return `${API}/projects/${item.projectId}/measure/live/session`;
  return null;
};

export const OfflineProvider = ({ children }) => {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [qsConflicts, setQsConflicts] = useState([]);
  const syncingRef = React.useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      setPendingCount(await countCaptures());
    } catch (e) { /* IndexedDB unavailable */ }
  }, []);

  const queueCapture = useCallback(async (opts) => {
    const rec = await enqueueCapture(opts);
    await refreshCount();
    return rec;
  }, [refreshCount]);

  const clearQsConflicts = useCallback(() => setQsConflicts([]), []);

  // Replay a queued Quality/Safety operation: upload offline photos first, then the
  // conflict-aware sync endpoint. Returns { removed, conflicts }.
  const syncQsItem = useCallback(async (item) => {
    const { opType, opPayload, photos } = item.payload || {};
    const payload = { ...(opPayload || {}) };
    // Upload any photos captured offline (base64 data URLs) → real evidence ids.
    const uploaded = [];
    for (const dataUrl of photos || []) {
      try {
        const fd = new FormData();
        const [meta, b64] = dataUrl.split(',');
        const mime = (meta.match(/:(.*?);/) || [])[1] || 'image/jpeg';
        const bin = atob(b64); const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        const file = new File([arr], `offline-${Date.now()}.jpg`, { type: mime });
        fd.append('file', file);
        const r = await axios.post(`${API}/projects/${item.projectId}/qs/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(r.data.evidenceId);
      } catch (e) { /* keep going; a failed photo shouldn't block the record */ }
    }
    if (uploaded.length) payload.evidenceIds = [...(payload.evidenceIds || []), ...uploaded];
    const body = { operations: [{ opId: item.id, type: opType, payload }] };
    const res = await axios.post(`${API}/projects/${item.projectId}/qs/offline/sync`, body);
    const result = (res.data.results || [])[0] || {};
    if (result.status === 'error') return { removed: false, conflicts: [] };
    if (result.status === 'conflict' || result.status === 'merged') {
      return { removed: true, conflicts: (result.conflicts || []).map((c) => ({ ...c, recordId: result.recordId, opType })) };
    }
    return { removed: true, conflicts: [] };
  }, []);

  const sync = useCallback(async () => {
    if (syncingRef.current) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    const items = await getAllCaptures();
    if (items.length === 0) { await refreshCount(); return; }
    syncingRef.current = true;
    setSyncing(true);
    let ok = 0;
    let failed = 0;
    const conflicts = [];
    for (const item of items) {
      if (item.type === 'qs-op') {
        try {
          const { removed, conflicts: c } = await syncQsItem(item);
          if (removed) { await removeCapture(item.id); ok += 1; }
          else failed += 1;
          if (c && c.length) conflicts.push(...c);
        } catch (e) {
          const status = e.response?.status;
          if (status && status >= 400 && status < 500 && status !== 401 && status !== 429) { await removeCapture(item.id); }
          failed += 1;
        }
        continue;
      }
      const url = endpointFor(item);
      if (!url) { await removeCapture(item.id); continue; }
      try {
        await axios.post(url, item.payload);
        await removeCapture(item.id);
        ok += 1;
      } catch (e) {
        // Auth/validation errors are permanent — drop to avoid an infinite loop.
        const status = e.response?.status;
        if (status && status >= 400 && status < 500 && status !== 401 && status !== 429) {
          await removeCapture(item.id);
        }
        failed += 1;
      }
    }
    syncingRef.current = false;
    setSyncing(false);
    await refreshCount();
    if (conflicts.length) setQsConflicts((prev) => [...prev, ...conflicts]);
    if (ok > 0 && conflicts.length === 0) toast.success(`Synced ${ok} offline capture${ok > 1 ? 's' : ''}`);
    if (ok > 0 && conflicts.length > 0) toast.warning(`Synced ${ok} — ${conflicts.length} field conflict${conflicts.length > 1 ? 's' : ''} need review`);
    if (failed > 0 && ok === 0) toast.error('Could not sync offline captures yet — will retry');
  }, [refreshCount, syncQsItem]);

  useEffect(() => {
    refreshCount();
    const goOnline = () => { setOnline(true); sync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Attempt a sync on mount in case items were queued in a previous session.
    if (navigator.onLine) sync();

    // Native-only augmentation (Capacitor Network is more reliable inside a
    // WebView than navigator.onLine). No-op in a browser — web path unchanged.
    let removeNativeListener;
    if (isNative()) {
      getOnlineStatus().then((connected) => setOnline(connected)).catch(() => {});
      removeNativeListener = addNetworkListener((connected) => {
        setOnline(connected);
        if (connected) sync();
      });
    }

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      if (removeNativeListener) removeNativeListener();
    };
  }, []);

  const value = { online, pendingCount, syncing, queueCapture, sync, refreshCount, qsConflicts, clearQsConflicts };
  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};
